import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
	Calendar,
	Clock,
	Video,
	ExternalLink,
	AlertCircle,
	CheckCircle,
	MessageCircle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

interface VirtualMeetingProps {
	// Appointment-based props (for dashboard/bookings)
	appointment?: {
		id: string;
		appointment_date: string;
		meeting_link: string;
		meeting_id: string;
		status: string;
		notes?: string;
		doula?: {
			full_name: string;
			email: string;
		};
		parent?: {
			full_name: string;
			email: string;
		};
	};
	onClose?: () => void;

	// Conversation-based props (for message threads)
	conversationId?: string;
	participantName?: string;
	userType?: "parent" | "doula";
	onBackToMessages?: () => void;
	doulaUserId?: string; // Add this to avoid direct DB call
}

export const VirtualMeeting: React.FC<VirtualMeetingProps> = ({
	appointment,
	onClose,
	conversationId,
	participantName,
	userType,
	onBackToMessages,
	doulaUserId,
}) => {
	const { user, profile } = useAuth();
	const [meetingStatus, setMeetingStatus] = useState<
		"upcoming" | "ready" | "in-progress" | "ended"
	>("upcoming");
	const [timeUntilMeeting, setTimeUntilMeeting] = useState<string>("");
	const [canJoin, setCanJoin] = useState(false);
	const [conversationAppointments, setConversationAppointments] = useState<
		any[]
	>([]);
	const [loadingAppointments, setLoadingAppointments] = useState(false);

	// Determine if this is appointment-based or conversation-based
	const isAppointmentMode = !!appointment;
	const isConversationMode = !!conversationId;

	// Load appointments for conversation mode
	useEffect(() => {
		if (isConversationMode && conversationId && profile && doulaUserId) {
			loadConversationAppointments();
		}
	}, [isConversationMode, conversationId, profile, doulaUserId]);

	// Meeting status check for appointment mode
	useEffect(() => {
		// Only run meeting status checks for appointment mode
		if (!isAppointmentMode || !appointment?.appointment_date) return;

		const checkMeetingStatus = () => {
			const now = new Date();
			const meetingTime = new Date(appointment.appointment_date);
			const timeDiff = meetingTime.getTime() - now.getTime();

			// Allow joining 10 minutes before the meeting
			const canJoinEarly =
				timeDiff <= 10 * 60 * 1000 && timeDiff > -30 * 60 * 1000; // 10 min before to 30 min after

			if (timeDiff > 0) {
				// Meeting is upcoming
				const hours = Math.floor(timeDiff / (1000 * 60 * 60));
				const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

				if (hours > 0) {
					setTimeUntilMeeting(`${hours}h ${minutes}m until meeting`);
				} else {
					setTimeUntilMeeting(`${minutes}m until meeting`);
				}

				setMeetingStatus("upcoming");
				setCanJoin(canJoinEarly);
			} else if (timeDiff > -30 * 60 * 1000) {
				// Meeting is in progress (within 30 minutes of start time)
				setTimeUntilMeeting("Meeting in progress");
				setMeetingStatus("in-progress");
				setCanJoin(true);
			} else {
				// Meeting has ended
				setTimeUntilMeeting("Meeting has ended");
				setMeetingStatus("ended");
				setCanJoin(false);
			}
		};

		checkMeetingStatus();
		const interval = setInterval(checkMeetingStatus, 60000); // Update every minute

		return () => clearInterval(interval);
	}, [appointment?.appointment_date, isAppointmentMode]);

	const loadConversationAppointments = async () => {
		if (!profile || !conversationId || !doulaUserId) {
			return;
		}

		try {
			setLoadingAppointments(true);

			// Get auth session for edge function call
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (!session) {
				return;
			}

			// Use get-parent-appointments edge function
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-parent-appointments`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${session.access_token}`,
					},
					body: JSON.stringify({ parentUserId: profile.id }),
				},
			);

			const result = await response.json();

			if (result.data) {
				// Filter appointments for this specific doula
				const doulaAppointments = result.data.filter(
					(apt: any) => apt.doula_user_id === doulaUserId,
				);

				setConversationAppointments(doulaAppointments);
			}
		} catch (error) {
			// Error loading conversation appointments
		} finally {
			setLoadingAppointments(false);
		}
	};

	const handleJoinMeeting = () => {
		if (appointment?.meeting_link) {
			window.open(appointment.meeting_link, "_blank", "noopener,noreferrer");
		}
	};

	const formatMeetingTime = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleString("en-US", {
			weekday: "long",
			year: "numeric",
			month: "long",
			day: "numeric",
			hour: "numeric",
			minute: "2-digit",
			timeZoneName: "short",
		});
	};

	const getStatusColor = () => {
		switch (meetingStatus) {
			case "upcoming":
				return "bg-blue-100 text-blue-800";
			case "ready":
				return "bg-green-100 text-green-800";
			case "in-progress":
				return "bg-orange-100 text-orange-800";
			case "ended":
				return "bg-gray-100 text-gray-800";
			default:
				return "bg-gray-100 text-gray-800";
		}
	};

	const getStatusIcon = () => {
		switch (meetingStatus) {
			case "upcoming":
				return <Clock className="h-4 w-4" />;
			case "ready":
				return <CheckCircle className="h-4 w-4" />;
			case "in-progress":
				return <Video className="h-4 w-4" />;
			case "ended":
				return <Calendar className="h-4 w-4" />;
			default:
				return <Calendar className="h-4 w-4" />;
		}
	};

	// Handle edge case where no mode is determined
	if (!isAppointmentMode && !isConversationMode) {
		return (
			<div className="p-4 h-full">
				<Card className="p-6">
					<div className="text-center space-y-4">
						<AlertCircle className="h-12 w-12 text-gray-400 mx-auto" />
						<h3 className="text-lg font-semibold text-gray-900">
							No Meeting Data
						</h3>
						<p className="text-gray-600">
							Unable to load meeting information. Please try again.
						</p>
						{onBackToMessages && (
							<Button onClick={onBackToMessages} variant="outline">
								Back to Messages
							</Button>
						)}
						{onClose && (
							<Button onClick={onClose} variant="outline">
								Close
							</Button>
						)}
					</div>
				</Card>
			</div>
		);
	}

	// Render different UI based on mode
	if (isConversationMode) {
		// Conversation mode - show actual meetings/appointments with this doula
		return (
			<div className="p-4 h-full overflow-y-auto">
				<div className="space-y-4">
					<div className="flex items-center justify-between">
						<h3 className="text-lg font-semibold text-gray-900">
							Meetings with {participantName}
						</h3>
						{onBackToMessages && (
							<Button onClick={onBackToMessages} variant="outline" size="sm">
								Back to Messages
							</Button>
						)}
					</div>

					{loadingAppointments ? (
						<Card className="p-6">
							<div className="text-center">
								<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
								<p className="text-gray-600">Loading meetings...</p>
							</div>
						</Card>
					) : conversationAppointments.length === 0 ? (
						<Card className="p-6">
							<div className="text-center space-y-4">
								<Video className="h-12 w-12 text-gray-400 mx-auto" />
								<h4 className="text-lg font-semibold text-gray-900">
									No Meetings Scheduled
								</h4>
								<p className="text-gray-600">
									You don't have any meetings scheduled with {participantName}{" "}
									yet.
								</p>
								<div className="space-y-2 text-sm text-gray-500">
									<p>
										• Virtual meetings are available for confirmed appointments
									</p>
									<p>• Google Meet links are automatically generated</p>
									<p>• Both parties receive calendar invitations</p>
								</div>
								<Button
									onClick={() => (window.location.href = "/search-doulas")}
									className="bg-primary-600 hover:bg-primary-700"
								>
									Book Appointment
								</Button>
							</div>
						</Card>
					) : (
						<div className="space-y-4">
							{conversationAppointments.map((appointment) => {
								const meetingTime = new Date(appointment.appointment_date);
								const now = new Date();
								const canJoin =
									appointment.meeting_link &&
									meetingTime.getTime() - now.getTime() <= 10 * 60 * 1000 && // 10 min before
									meetingTime.getTime() - now.getTime() > -30 * 60 * 1000; // 30 min after

								return (
									<Card key={appointment.id} className="p-4">
										<div className="space-y-3">
											{/* Meeting Header */}
											<div className="flex items-center justify-between">
												<h4 className="font-semibold text-gray-900">
													{appointment.doula_listing?.title || "Doula Session"}
												</h4>
												<Badge
													className={
														appointment.status === "completed"
															? "bg-green-100 text-green-800"
															: appointment.status === "accepted"
																? "bg-blue-100 text-blue-800"
																: appointment.status === "pending"
																	? "bg-yellow-100 text-yellow-800"
																	: "bg-gray-100 text-gray-800"
													}
												>
													{appointment.status}
												</Badge>
											</div>

											{/* Meeting Details */}
											<div className="space-y-2 text-sm text-gray-600">
												<div className="flex items-center">
													<Calendar className="h-4 w-4 mr-2" />
													<span>
														{formatMeetingTime(appointment.appointment_date)}
													</span>
												</div>
												<div className="flex items-center">
													<Clock className="h-4 w-4 mr-2" />
													<span>
														Duration: {appointment.duration_minutes || 60}{" "}
														minutes
													</span>
												</div>
												{appointment.notes && (
													<div className="bg-gray-50 p-2 rounded text-xs">
														<strong>Notes:</strong> {appointment.notes}
													</div>
												)}
											</div>

											{/* Meeting Actions */}
											{appointment.meeting_link && (
												<div className="pt-3 border-t border-gray-100">
													{appointment.status === "accepted" && (
														<>
															{canJoin ? (
																<div className="space-y-3">
																	<div className="bg-green-50 border border-green-200 rounded-lg p-3">
																		<div className="flex items-center">
																			<CheckCircle className="h-4 w-4 text-green-600 mr-2" />
																			<p className="text-sm text-green-800">
																				You can join the meeting now!
																			</p>
																		</div>
																	</div>
																	<Button
																		onClick={() =>
																			window.open(
																				appointment.meeting_link,
																				"_blank",
																				"noopener,noreferrer",
																			)
																		}
																		className="w-full bg-green-600 hover:bg-green-700"
																	>
																		<Video className="h-4 w-4 mr-2" />
																		Join Meeting
																	</Button>
																</div>
															) : (
																<div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
																	<div className="flex items-center">
																		<AlertCircle className="h-4 w-4 text-blue-600 mr-2" />
																		<p className="text-sm text-blue-800">
																			Meeting available 10 minutes before start
																			time
																		</p>
																	</div>
																</div>
															)}
														</>
													)}

													{appointment.status === "completed" && (
														<div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
															<div className="flex items-center">
																<CheckCircle className="h-4 w-4 text-gray-600 mr-2" />
																<p className="text-sm text-gray-800">
																	Meeting completed
																</p>
															</div>
														</div>
													)}

													<div className="text-xs text-gray-500 mt-2">
														Meeting ID: {appointment.meeting_id || "TBD"}
													</div>
												</div>
											)}

											{appointment.status === "pending" && (
												<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
													<div className="flex items-center">
														<AlertCircle className="h-4 w-4 text-yellow-600 mr-2" />
														<p className="text-sm text-yellow-800">
															Waiting for doula to confirm this appointment
														</p>
													</div>
												</div>
											)}
										</div>
									</Card>
								);
							})}
						</div>
					)}
				</div>
			</div>
		);
	}

	// Appointment mode - show full meeting interface (original functionality)
	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
			<motion.div
				initial={{ opacity: 0, scale: 0.9 }}
				animate={{ opacity: 1, scale: 1 }}
				exit={{ opacity: 0, scale: 0.9 }}
				className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
			>
				<div className="p-6">
					{/* Header */}
					<div className="flex items-center justify-between mb-6">
						<h2 className="text-xl font-bold">Virtual Meeting</h2>
						<button
							onClick={onClose}
							className="text-gray-400 hover:text-gray-600"
						>
							×
						</button>
					</div>

					{/* Meeting Status */}
					{isAppointmentMode && (
						<div className="mb-6">
							<div
								className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor()}`}
							>
								{getStatusIcon()}
								<span className="ml-2">{timeUntilMeeting}</span>
							</div>
						</div>
					)}

					{/* Meeting Details */}
					<Card className="p-6 mb-6">
						<div className="space-y-4">
							<div>
								<h3 className="font-semibold text-lg mb-2">Meeting Details</h3>
								<div className="space-y-2 text-sm text-gray-600">
									{appointment?.appointment_date && (
										<div className="flex items-center">
											<Calendar className="h-4 w-4 mr-2" />
											<span>
												{formatMeetingTime(appointment.appointment_date)}
											</span>
										</div>
									)}
									<div className="flex items-center">
										<Clock className="h-4 w-4 mr-2" />
										<span>Duration: 30 minutes</span>
									</div>
									{appointment?.doula && (
										<div>
											<span className="font-medium">Doula:</span>{" "}
											{appointment.doula.full_name}
										</div>
									)}
									{appointment?.parent && (
										<div>
											<span className="font-medium">Parent:</span>{" "}
											{appointment.parent.full_name}
										</div>
									)}
								</div>
							</div>

							{appointment?.notes && (
								<div>
									<h4 className="font-medium mb-2">Notes</h4>
									<p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
										{appointment.notes}
									</p>
								</div>
							)}
						</div>
					</Card>

					{/* Meeting Link */}
					{appointment?.meeting_link && (
						<Card className="p-6 mb-6">
							<div className="space-y-4">
								<h3 className="font-semibold">Join Meeting</h3>

								{meetingStatus === "upcoming" && !canJoin && (
									<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
										<div className="flex items-center">
											<AlertCircle className="h-5 w-5 text-blue-600 mr-2" />
											<p className="text-sm text-blue-800">
												You can join the meeting 10 minutes before the scheduled
												start time.
											</p>
										</div>
									</div>
								)}

								{canJoin && (
									<div className="space-y-4">
										<div className="bg-green-50 border border-green-200 rounded-lg p-4">
											<div className="flex items-center">
												<CheckCircle className="h-5 w-5 text-green-600 mr-2" />
												<p className="text-sm text-green-800">
													{meetingStatus === "in-progress"
														? "Meeting is in progress. You can join now."
														: "You can join the meeting now."}
												</p>
											</div>
										</div>

										<Button
											onClick={handleJoinMeeting}
											className="w-full bg-blue-600 hover:bg-blue-700"
											size="lg"
										>
											<Video className="h-5 w-5 mr-2" />
											Join Google Meet
											<ExternalLink className="h-4 w-4 ml-2" />
										</Button>
									</div>
								)}

								{meetingStatus === "ended" && (
									<div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
										<div className="flex items-center">
											<Calendar className="h-5 w-5 text-gray-600 mr-2" />
											<p className="text-sm text-gray-800">
												This meeting has ended. You can view the meeting link
												below for reference.
											</p>
										</div>
									</div>
								)}

								<div className="text-xs text-gray-500">
									Meeting ID: {appointment?.meeting_id}
								</div>
							</div>
						</Card>
					)}

					{/* Instructions */}
					<Card className="p-6 mb-6">
						<h3 className="font-semibold mb-3">Meeting Instructions</h3>
						<div className="space-y-2 text-sm text-gray-600">
							<p>• Make sure you have a stable internet connection</p>
							<p>• Test your microphone and camera before joining</p>
							<p>
								• Join the meeting 5-10 minutes early to ensure everything works
							</p>
							<p>• The meeting will be hosted by Gaia (app owner)</p>
							<p>• Both parties will receive separate email invitations</p>
						</div>
					</Card>

					{/* Actions */}
					<div className="flex justify-end space-x-3">
						<Button variant="outline" onClick={onClose}>
							Close
						</Button>
						{canJoin && appointment?.meeting_link && (
							<Button onClick={handleJoinMeeting}>
								<Video className="h-4 w-4 mr-2" />
								Join Meeting
							</Button>
						)}
					</div>
				</div>
			</motion.div>
		</div>
	);
};
